import Handsontable from 'handsontable/base';
import type { Action as HotUndoRedoAction } from 'handsontable/plugins/undoRedo';

import { TableAxis, TableChangeSource, type MNTableContent } from '../../../../../../common/table/TableTypes';
import { convertHotInstanceToRaw } from '../persistence/hotTablePersistenceUtils';
import { applyRawTableContentToHotInstance } from '../persistence/hotTableRehydrationUtils';
import { restoreHyperFormulaUndoRedoStack, type HyperFormulaUndoRedoStacks } from './hotTableUndoRedoHyperFormulaUtils';
import { HotTableUndoRedoActionType } from './hotTableUndoRedoRichCellDataUtils';

type MoveSelection = {
    axis: TableAxis;
    startIndex: number;
    endIndex: number;
};

const HOT_ROW_MOVE_ACTION_TYPE = 'row_move';
const HOT_COLUMN_MOVE_ACTION_TYPE = 'col_move';
const HOT_MOVE_ACTION_TYPES = [HOT_ROW_MOVE_ACTION_TYPE, HOT_COLUMN_MOVE_ACTION_TYPE];

export type HotTableMoveUndoRedoAction = {
    actionType: typeof HotTableUndoRedoActionType.MILANOTE_MOVE;
    undo: (instance: Handsontable, callback: () => void) => void;
    redo: (instance: Handsontable, callback: () => void) => void;
};

type HotUndoRedoStackAction = {
    actionType: string;
};

type HotUndoRedoPluginWithDone = {
    done?: (wrappedAction: () => HotTableMoveUndoRedoAction, source?: string) => void;
    doneActions?: Array<HotUndoRedoAction | HotTableMoveUndoRedoAction | HotUndoRedoStackAction>;
};

/**
 * Applies the row or column selection that should be visible after a move replay.
 */
const applyMoveSelection = (hotInstance: Handsontable, selection: MoveSelection): void => {
    hotInstance.deselectCell();

    selection.axis === TableAxis.ROW
        ? hotInstance.selectRows(selection.startIndex, selection.endIndex)
        : hotInstance.selectColumns(selection.startIndex, selection.endIndex);
};

/**
 * Builds the header selection ranges before and after a row or column move.
 */
const createMoveSelections = (
    axis: TableAxis,
    movedIndices: number[],
    finalIndex: number,
): {
    beforeSelection: MoveSelection;
    afterSelection: MoveSelection;
} => ({
    beforeSelection: {
        axis,
        startIndex: movedIndices[0],
        endIndex: movedIndices[movedIndices.length - 1],
    },
    afterSelection: {
        axis,
        startIndex: finalIndex,
        endIndex: finalIndex + movedIndices.length - 1,
    },
});

/**
 * Rehydrates a snapshot without creating a new HOT undo action, then restores the matching moved selection.
 */
const applyMoveSnapshot = (
    hotInstance: Handsontable,
    snapshot: MNTableContent,
    gridSize: number,
    selection: MoveSelection,
    callback: () => void,
): void => {
    applyRawTableContentToHotInstance(
        hotInstance,
        snapshot,
        gridSize,
        'update',
        TableChangeSource.MOVE_SNAPSHOT_REPLAY,
    );

    requestAnimationFrame(() => {
        if (hotInstance.isDestroyed) {
            callback();
            return;
        }

        applyMoveSelection(hotInstance, selection);
        callback();
    });
};

/**
 * HOT pushes native move undo actions that only replay mapper changes.
 * Once Milanote has flattened the move back into the raw source data, those native actions become stale.
 */
const removeNativeMoveUndoAction = (undoRedoPlugin: HotUndoRedoPluginWithDone | undefined): void => {
    const doneActions = undoRedoPlugin?.doneActions;
    const latestAction = doneActions?.[doneActions.length - 1];
    if (!latestAction || !HOT_MOVE_ACTION_TYPES.includes(latestAction.actionType)) return;

    doneActions?.pop();
};

/**
 * Resets both HOT index mappers back to source-data order before Milanote reapplies the moved snapshot.
 */
const resetGridIndexMappers = (hotInstance: Handsontable): void => {
    hotInstance.rowIndexMapper.setIndexesSequence(
        new Array(hotInstance.countRows()).fill(0).map((_value, index) => index),
    );
    hotInstance.columnIndexMapper.setIndexesSequence(
        new Array(hotInstance.countCols()).fill(0).map((_value, index) => index),
    );
};

/**
 * - Captures before/after table snapshots for a move
 * - Flattens the moved order into HOT source data
 * - Replaces HOT's mapper-based undo entry with a Milanote-owned snapshot replay action
 */
export const reconcileMovedGridWithUndoRedo = ({
    hotInstance,
    beforeSnapshot,
    axis,
    movedIndices,
    finalIndex,
    gridSize,
    beforeHyperFormulaUndoRedoStack,
}: {
    hotInstance: Handsontable;
    beforeSnapshot: MNTableContent | null;
    axis: TableAxis;
    movedIndices: number[];
    finalIndex: number;
    gridSize: number;
    beforeHyperFormulaUndoRedoStack?: HyperFormulaUndoRedoStacks | null;
}): void => {
    if (!movedIndices.length) return;
    if (!beforeSnapshot) return;

    // The "after" data is the current table state, because this function is executed in the
    //  "afterGridMovement" of useTableCellEditingHandlers.ts (so the index mappers have been modified)
    const undoRedoPlugin = hotInstance.getPlugin('undoRedo') as HotUndoRedoPluginWithDone | undefined;

    if (!undoRedoPlugin) return;

    const afterSnapshot = convertHotInstanceToRaw(hotInstance, gridSize);

    const { beforeSelection, afterSelection } = createMoveSelections(axis, movedIndices, finalIndex);

    // Reset the index mappers back to the default state - this is because we don't store the index mappers
    //  in our redux data, so when we rehydrate the table, it's starting with the default index mapper
    resetGridIndexMappers(hotInstance);

    // Now that we're at the default index mapper - put the table snapshot *data* back into the table
    applyMoveSnapshot(hotInstance, afterSnapshot, gridSize, afterSelection, () => {});

    // applyMoveSnapshot calls HyperFormula's "setSheetContent" within sub-functions & this will apparently add an
    //  entry onto *HyperFormula's* undo/redo stack. We don't want this to happen as it will cause it and
    //  Handsontable's undo/redo to become out of sync, so we restore the prior undo/redo stack here
    const nextHyperFormulaUndoRedoStack = beforeHyperFormulaUndoRedoStack
        ? { undoStack: beforeHyperFormulaUndoRedoStack.undoStack, redoStack: [] }
        : null;
    restoreHyperFormulaUndoRedoStack(hotInstance, nextHyperFormulaUndoRedoStack);

    // Remove any handsontable native Undo/Redo handling because we're now handling it ourselves
    removeNativeMoveUndoAction(undoRedoPlugin);

    undoRedoPlugin.done?.(
        (): HotTableMoveUndoRedoAction => ({
            actionType: HotTableUndoRedoActionType.MILANOTE_MOVE,
            undo: (instance, undoneCallback) => {
                applyMoveSnapshot(instance, beforeSnapshot, gridSize, beforeSelection, undoneCallback);
            },
            redo: (instance, redoneCallback) => {
                applyMoveSnapshot(instance, afterSnapshot, gridSize, afterSelection, redoneCallback);
            },
        }),
        TableChangeSource.MOVE_SNAPSHOT_REPLAY,
    );
};
